import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { portalTheme as t, ensurePortalFonts, ensurePortalKeyframes } from './theme';
import {
  portalFetch, clearPortalToken, getPortalToken,
  getPortalImpersonator, clearPortalImpersonator,
} from './auth';
import { tooLargeMessage, MAX_UPLOAD_LABEL } from './uploadLimits';

/* Client portal dashboard.
 *
 * Self-contained — no imports from `routes/`, `app/`, or `features/`.
 * Talks to the API only through portalFetch (which carries the Bearer
 * token), so when the portal is extracted into its own app this file
 * moves with the rest of the directory unchanged.
 *
 * Layout: welcome banner, 4-stage workflow stepper, advisor card, then a
 * "What's next" card with the document upload list. Designed to feel
 * like a private-bank statement portal — restrained, serif headings,
 * generous space, no decorative noise.
 */

// Same four stages the firm uses in conversation. Order matters — the
// stepper uses indices to determine completed vs current vs future.
const WORKFLOW_STAGES = [
  { key: 'discovery', label: 'Discovery', sub: 'We gather your documents and understand your goals.' },
  { key: 'design',    label: 'Design',    sub: 'Your advisor builds a personalized bucket plan.' },
  { key: 'delivery',  label: 'Delivery',  sub: 'We coordinate the moves to put the plan into action.' },
  { key: 'dedicate',  label: 'Dedicate',  sub: 'Ongoing care: review, refine, and support.' },
];

function stageIndex(key) {
  const i = WORKFLOW_STAGES.findIndex(s => s.key === key);
  return i >= 0 ? i : 0;
}

function fullName(c) {
  return `${c?.first_name || ''} ${c?.last_name || ''}`.trim() || c?.email || 'Client';
}

function firstName(c) {
  return (c?.first_name || '').trim() || (c?.email || '').split('@')[0] || 'there';
}

function formatBytes(n) {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// Snapshot a File's bytes into memory before building the upload request.
// Appending the raw File makes Chromium re-read it from disk at network-send
// time and abort with ERR_UPLOAD_FILE_CHANGED if the file's size/mtime shifted
// in between. The read itself can throw a NotFoundError DOMException when the
// file is a cloud-sync placeholder (OneDrive/Dropbox "online-only" files get
// dehydrated after a while). The first failed read usually kicks off hydration
// in the OS, so retry a couple of times with a pause before giving up.
// Returns an ArrayBuffer, or null if every attempt failed.
async function readFileWithRetry(file) {
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      return await file.arrayBuffer();
    } catch {
      // eslint-disable-next-line no-await-in-loop
      await new Promise(r => setTimeout(r, 700 * (attempt + 1)));
    }
  }
  return null;
}

const UNREADABLE_FILE_MESSAGE = 'We couldn’t read that file from your device — if it lives in a synced folder (e.g. OneDrive), open it once or mark it “Always keep on this device”, then choose it again.';

// The general drag-and-drop bucket's sentinel type_key (mirrors the server's
// document_catalog.GENERAL_UNSORTED_KEY).
const GENERAL_UNSORTED_KEY = 'general_unsorted';

export default function PortalDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [agreement, setAgreement] = useState(null);
  const [signing, setSigning] = useState(false);
  const [signErr, setSignErr] = useState(null);
  // General drag-and-drop area: how many files are currently mid-upload (a
  // count, not an id, since several can be in flight from one multi-file drop)
  // and any per-batch error.
  const [generalUploadingCount, setGeneralUploadingCount] = useState(0);
  const [generalUploadError, setGeneralUploadError] = useState(null);
  const [isDraggingGeneral, setIsDraggingGeneral] = useState(false);
  // "I am done uploading documents" — swaps the checklist for a thank-you
  // screen (driven by client.documents_submitted_at) while this call and any
  // "Edit existing documents" call are in flight.
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  // Documents the advisory team has shared WITH this client (the other
  // direction from every upload above). `sharedViewer` holds a blob: URL for
  // the in-page viewer (or, with `video: true`, a streaming playback link), or
  // null.
  const [sharedViewer, setSharedViewer] = useState(null);
  const [sharedError, setSharedError] = useState(null);
  // Which shared document is being fetched and how — `{id, mode}` or null.
  // A document is only shown once its bytes are in hand, which for a big PDF
  // is a couple of seconds of nothing happening; this drives the spinner that
  // fills that gap.
  const [openingShared, setOpeningShared] = useState(null);
  // Which half of the portal is on screen: 'requested' (things we need from
  // them) or 'shared' (things we've sent). null until the profile loads and
  // the rule below picks an opening tab — after that it is the person's own
  // choice and nothing reassigns it.
  const [tab, setTab] = useState(null);

  useEffect(() => { ensurePortalFonts(); ensurePortalKeyframes(); }, []);

  // Fetch the full profile + docs from the server. Refresh-safe: if the
  // token is missing or expired we bounce back to /portal/login.
  const loadProfile = async () => {
    setLoadErr(null);
    try {
      if (!getPortalToken()) {
        navigate('/portal/login', { replace: true });
        return;
      }
      const data = await portalFetch('/api/portal/me');
      setProfile(data);
      // Agreement is optional (only fee-bearing clients have one); never let a
      // failure here block the portal from loading.
      try {
        const ag = await portalFetch('/api/portal/agreement');
        setAgreement(ag?.agreement || null);
      } catch { /* non-fatal */ }
    } catch (e) {
      if (e.status === 401) {
        navigate('/portal/login', { replace: true });
        return;
      }
      setLoadErr(e.message || 'Could not load your portal.');
    }
  };

  // Seed from the router state passed by the login page (avoids a flash
  // of empty UI), then refresh from the server for the real source of
  // truth.
  useEffect(() => {
    if (location.state?.client) {
      setProfile(p => p || { client: location.state.client, requested_documents: [], household_members: [] });
    }
    // Run once on mount. loadProfile closes over `navigate` (a stable
    // useNavigate result) and only sets state, so an empty dep array is
    // intentional here.
    loadProfile();
  }, []);

  // While any general-drop upload is still being sorted (Textract/Bedrock
  // classification in flight), poll so the tile updates once it lands —
  // either auto-filed under a matched category or settled in needs-review.
  // Declared before the loading/error early-returns below since hooks must run
  // unconditionally; `profile` is optional-chained since it starts out null.
  const generalDocForPolling = (profile?.requested_documents || []).find(d => d.type_key === GENERAL_UNSORTED_KEY);
  const isSorting = (generalDocForPolling?.uploads || []).some(u => u.sorting);
  useEffect(() => {
    if (!isSorting) return undefined;
    const id = window.setInterval(() => { loadProfile(); }, 4000);
    return () => window.clearInterval(id);
  }, [isSorting]);

  // Set when an employee is "logged in as" this client. Returning just
  // discards the portal token — the employee's own intranet session is a
  // separate token and was never touched, so they land straight back in it.
  const impersonator = getPortalImpersonator();

  // Close the impersonation_audit row (best-effort) before dropping the token,
  // so the session's end is recorded like the employee and B2B flows. No-op
  // server-side for a normal (non-impersonated) session.
  const endImpersonation = async () => {
    if (!impersonator) return;
    try { await portalFetch('/api/portal/impersonate/return', { method: 'POST' }); }
    catch { /* best effort — don't block the exit on it */ }
    clearPortalImpersonator();
  };

  const handleSignOut = async () => {
    await endImpersonation();
    clearPortalToken();
    navigate('/portal/login', { replace: true });
  };

  // Back to the client's page on the Work side, where the "Log in as" button is.
  const returnToWork = async () => {
    await endImpersonation();
    clearPortalToken();
    window.location.assign(
      impersonator?.client_public_id
        ? `/work/clients/${impersonator.client_public_id}`
        : '/work/clients',
    );
  };

  const handleUpload = async (request, file) => {
    if (!file) return;
    setUploadError(null);

    // Checked before reading a byte. The server rejects oversized files too
    // (it is the authority), but only after the whole thing has been sent —
    // which on a home connection is minutes of progress bar for a file that
    // was never going to be accepted.
    const tooLarge = tooLargeMessage(file);
    if (tooLarge) {
      setUploadError(tooLarge);
      return;
    }

    setUploadingId(request.id);

    const buf = await readFileWithRetry(file);
    if (buf === null) {
      setUploadError(UNREADABLE_FILE_MESSAGE);
      setUploadingId(null);
      return;
    }

    try {
      const blob = new Blob([buf], { type: file.type || 'application/octet-stream' });
      const fd = new FormData();
      fd.append('file', blob, file.name);
      await portalFetch(`/api/portal/documents/${request.id}/upload`, {
        method: 'POST',
        body: fd,
      });
      await loadProfile();
    } catch (e) {
      setUploadError(e.message || 'Upload failed. Please try again.');
    } finally {
      setUploadingId(null);
    }
  };

  const handleDeleteUpload = async (upload) => {
    setUploadError(null);
    try {
      await portalFetch(`/api/portal/uploads/${upload.id}`, { method: 'DELETE' });
      await loadProfile();
    } catch (e) {
      setUploadError(e.message || 'Could not remove that file.');
    }
  };

  // General drag-and-drop: no request pre-chosen. Upload each dropped/picked
  // file sequentially (keeps the read-retry logic simple and avoids piling up
  // parallel multipart requests), refreshing after each so the list fills in
  // incrementally rather than waiting for the whole batch.
  const handleGeneralUpload = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (files.length === 0) return;
    setGeneralUploadError(null);
    const errors = [];
    for (const file of files) {
      // Same pre-check as the per-request path. Done inside the loop so one
      // oversized file in a multi-file drop is reported and skipped while the
      // rest still upload, rather than failing the whole batch.
      const tooLarge = tooLargeMessage(file);
      if (tooLarge) {
        errors.push(`${file.name}: ${tooLarge}`);
        continue;
      }
      setGeneralUploadingCount(c => c + 1);
      try {
        // eslint-disable-next-line no-await-in-loop
        const buf = await readFileWithRetry(file);
        if (buf === null) {
          errors.push(`${file.name}: ${UNREADABLE_FILE_MESSAGE}`);
          continue;
        }
        const blob = new Blob([buf], { type: file.type || 'application/octet-stream' });
        const fd = new FormData();
        fd.append('file', blob, file.name);
        // eslint-disable-next-line no-await-in-loop
        await portalFetch('/api/portal/documents/upload', { method: 'POST', body: fd });
        // eslint-disable-next-line no-await-in-loop
        await loadProfile();
      } catch (e) {
        errors.push(`${file.name}: ${e.message || 'Upload failed.'}`);
      } finally {
        setGeneralUploadingCount(c => Math.max(0, c - 1));
      }
    }
    if (errors.length) setGeneralUploadError(errors.join(' '));
  };

  const handleGeneralDrop = (e) => {
    e.preventDefault();
    setIsDraggingGeneral(false);
    handleGeneralUpload(e.dataTransfer?.files);
  };

  const handleSubmitDocuments = async () => {
    if (!window.confirm("Are you sure you're done uploading? Your advisor's office will be notified to review your documents.")) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      await portalFetch('/api/portal/documents/submit', { method: 'POST' });
      await loadProfile();
    } catch (e) {
      setSubmitError(e.message || 'Could not submit right now. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditDocuments = async () => {
    setSubmitError(null);
    setSubmitting(true);
    try {
      await portalFetch('/api/portal/documents/unsubmit', { method: 'POST' });
      await loadProfile();
    } catch (e) {
      setSubmitError(e.message || 'Could not reopen your documents. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Open something the advisory team shared with us. The server hands back a
  // single-use link; we fetch it here and hand the browser an in-memory
  // blob: URL rather than navigating to it, for the same reason the work side
  // does — a link the browser navigates to can be fetched by a PDF previewer
  // or a scanning proxy before the person's own click lands, burning its one
  // use. `disposition: 'inline'` opens it in the page, otherwise it saves.
  const openSharedDocument = async (doc, { download = false } = {}) => {
    // One at a time, and never twice. Each attempt mints a SINGLE-USE download
    // token: an impatient second click during the wait would mint a second
    // token and pull the file again, and on a slow connection that is exactly
    // when someone clicks twice.
    if (openingShared) return;
    setSharedError(null);
    setOpeningShared({ id: doc.id, mode: download ? 'download' : 'view' });
    try {
      // Videos are the exception to all of the above. Watching one gets a
      // reusable ~2 hour playback link the <video> element streams from
      // (it makes many requests, so a single-use link can't play it), and
      // downloading one goes straight from the one-time link to disk rather
      // than holding up to 2 GB in the tab's memory as a blob first.
      if (fileKind(doc).video) {
        if (download) {
          const res = await portalFetch(`/api/portal/shared/${doc.id}/download_url`);
          if (!res?.url) throw new Error('Could not download that video.');
          const a = document.createElement('a');
          a.href = res.url;
          a.download = res.filename || doc.title || 'video';
          document.body.appendChild(a);
          a.click();
          a.remove();
        } else {
          const res = await portalFetch(`/api/portal/shared/${doc.id}/stream_url`);
          if (!res?.url) throw new Error('Could not play that video.');
          setSharedViewer({ url: res.url, filename: doc.title || res.filename, video: true });
        }
        if (!doc.viewed) loadProfile().catch(() => {});
        return;
      }
      const qs = download ? '' : '?disposition=inline';
      const res = await portalFetch(`/api/portal/shared/${doc.id}/download_url${qs}`);
      if (!res?.url) throw new Error('Could not open that document.');
      const resp = await fetch(res.url);
      if (!resp.ok) throw new Error(`Could not retrieve the file (HTTP ${resp.status}).`);
      const blobUrl = URL.createObjectURL(await resp.blob());
      if (download) {
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = res.filename || doc.title || 'document';
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      } else {
        setSharedViewer({ url: blobUrl, filename: res.filename || doc.title });
      }
      // The server just recorded this as read. Re-read the profile so the
      // "New" mark and the tab's count clear behind the viewer rather than
      // lingering until the next visit. Non-fatal: the document is already
      // open, and a stale badge is not worth an error message.
      if (!doc.viewed) loadProfile().catch(() => {});
    } catch (e) {
      setSharedError(e.detail || e.message || 'Could not open that document.');
    } finally {
      setOpeningShared(null);
    }
  };

  const closeSharedViewer = () => {
    setSharedViewer(v => {
      if (v?.url && !v.video) URL.revokeObjectURL(v.url);
      return null;
    });
  };

  // Open the embedded DocuSign signing ceremony. Full-page redirect (iframes
  // are blocked by DocuSign); DocuSign returns the browser here when finished.
  const handleSignAgreement = async () => {
    setSignErr(null); setSigning(true);
    try {
      const { url } = await portalFetch('/api/portal/agreement/signing_url', {
        method: 'POST', body: JSON.stringify({ return_url: window.location.href }),
      });
      if (url) window.location.assign(url);
      else { setSigning(false); setSignErr('Could not open the signing session.'); }
    } catch (e) {
      setSignErr(e.detail || e.message || 'Could not open the signing session.');
      setSigning(false);
    }
  };

  if (loadErr) {
    return <ErrorView message={loadErr} onSignOut={handleSignOut} />;
  }
  if (!profile) {
    return <LoadingView />;
  }

  const {
    client, requested_documents: docs = [], household_members: members = [],
    shared_documents: sharedDocs = [],
  } = profile;
  const documentsSubmitted = !!client.documents_submitted_at;
  // The general drag-and-drop bucket is rendered in its own section below, not
  // mixed into the requested-items list (it isn't something the advisor asked
  // for — it's the client's own extra uploads awaiting classification/review).
  const specificDocs = docs.filter(d => d.type_key !== GENERAL_UNSORTED_KEY);
  const generalDoc = docs.find(d => d.type_key === GENERAL_UNSORTED_KEY) || null;
  const generalUploads = generalDoc?.uploads || [];
  // Surface the advisor's ad-hoc "custom" asks first — they're the specific,
  // often time-sensitive requests, so we don't want them buried under the
  // standard catalog items. Stable sort preserves order within each group.
  const orderedDocs = [...specificDocs].sort(
    (a, b) => (a.type_key === 'custom' ? 0 : 1) - (b.type_key === 'custom' ? 0 : 1),
  );
  const partner = (members || []).find(m => m.id !== client.id) || null;
  const currentIdx = stageIndex(client.workflow_status);
  const currentStage = WORKFLOW_STAGES[currentIdx];

  // A request is "satisfied" when it has at least one uploaded file. The
  // client can still add more files later, but at least one means they
  // delivered something for that category.
  const totalDocs = specificDocs.length;
  const uploadedDocs = specificDocs.filter(d => (d.uploads?.length || 0) > 0).length;
  const allUploaded = totalDocs > 0 && uploadedDocs === totalDocs;

  // ── Which tab opens ──────────────────────────────────────────────────────
  // Derived rather than set in an effect, so there is never a frame on the
  // wrong tab and nothing can reassign it behind the person: once `tab` is
  // set by a click it wins, and the rule below only decides the first view.
  //
  // The order is "what did this person come here to see":
  //   1. Something new we sent - they were almost certainly emailed about it.
  //   2. Something still outstanding from them.
  //   3. Anything we've sent, unread or not.
  //   4. Otherwise the upload side, which is where onboarding starts.
  const unreadShared = sharedDocs.filter(d => !d.viewed).length;
  const outstanding = totalDocs - uploadedDocs;
  const defaultTab = unreadShared > 0 ? 'shared'
    : (!documentsSubmitted && outstanding > 0) ? 'requested'
      : sharedDocs.length > 0 ? 'shared'
        : 'requested';
  const activeTab = tab || defaultTab;

  return (
    <div style={{
      minHeight: '100vh',
      background: t.canvas,
      color: t.ink,
      fontFamily: t.fontSans,
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Impersonation banner. Deliberately loud and above the brand stripe:
          everything below it is a real client's private portal, and any upload
          or submit made here is made as them. */}
      {impersonator && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
          background: t.gold, color: t.navy, padding: '0.55rem 1.5rem',
          fontSize: '0.85rem', fontWeight: 700, flexShrink: 0,
        }}>
          <i className="bi bi-incognito" style={{ fontSize: '1.05rem' }} />
          <span style={{ flex: 1, minWidth: 200 }}>
            Prosperity staff view - you&apos;re logged in as
            {impersonator.target_name || impersonator.target_email
              ? <> <strong>{impersonator.target_name || impersonator.target_email}</strong></>
              : ' this client'}. Anything you do here is done as them.
          </span>
          <button type="button" onClick={returnToWork} style={{
            background: t.navy, color: 'white', border: 'none', borderRadius: 6,
            padding: '0.3rem 0.8rem', fontWeight: 700, fontSize: '0.8rem',
            cursor: 'pointer', whiteSpace: 'nowrap',
          }}>
            <i className="bi bi-arrow-left me-1" />Return to the hub
          </button>
        </div>
      )}

      {/* Gold accent stripe at very top — same brand-frame device used on
          the login page so the two surfaces feel like the same product. */}
      <div style={{
        height: 2, flexShrink: 0,
        background: `linear-gradient(90deg, transparent 0%, ${t.gold} 50%, transparent 100%)`,
      }} />

      {/* Top bar */}
      <header style={{
        background: t.navy,
        color: 'white',
        padding: '0.85rem 1.5rem',
        display: 'flex', alignItems: 'center', gap: '0.75rem',
      }}>
        <div aria-hidden="true" style={{
          width: 28, height: 28, flexShrink: 0,
          background: t.gold,
          WebkitMask: 'url(/pca.png) center / contain no-repeat',
          mask: 'url(/pca.png) center / contain no-repeat',
        }} />
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
          <span style={{ fontWeight: 700, fontSize: '0.9rem', letterSpacing: '0.02em' }}>
            Prosperity Capital Advisors
          </span>
          <span style={{ color: t.goldSoft, fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.18em', marginTop: 2 }}>
            Client Portal
          </span>
        </div>
        <button
          type="button"
          onClick={handleSignOut}
          style={{
            marginLeft: 'auto', background: 'transparent', border: 'none',
            color: 'rgba(255,255,255,0.78)', fontSize: '0.75rem', cursor: 'pointer',
            letterSpacing: '0.08em', textTransform: 'uppercase',
          }}
        >
          Sign out
        </button>
      </header>

      <main style={{
        flex: 1,
        width: '100%',
        maxWidth: 1100,
        margin: '0 auto',
        padding: '2.5rem 1.5rem 3rem',
      }}>

        {/* Welcome row */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
          <div>
            <p style={{ color: t.muted, fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.22em', margin: 0 }}>
              Welcome back
            </p>
            <h1 style={{
              fontFamily: t.fontSerif, fontSize: '2rem', fontWeight: 700,
              color: t.navy, margin: '0.35rem 0 0', letterSpacing: '-0.01em',
              lineHeight: 1.1,
            }}>
              Good day, {firstName(client)}.
            </h1>
            <p style={{ color: t.inkSubtle, fontSize: '0.85rem', margin: '0.5rem 0 0', maxWidth: 540, lineHeight: 1.55 }}>
              You&apos;re in the <strong style={{ color: t.navy }}>{currentStage.label}</strong> phase
              {partner && (
                <> with <strong style={{ color: t.navy }}>{fullName(partner)}</strong></>
              )}.
              {' '}{currentStage.sub}
            </p>
          </div>
          {client.advisor_name && (
            <AdvisorCard
              name={client.advisor_name}
              photoUrl={client.advisor_photo_url}
              secondName={client.advisor_name_2}
              secondPhotoUrl={client.advisor_photo_url_2}
            />
          )}
        </div>

        {/* Stepper */}
        <WorkflowStepper currentIdx={currentIdx} />

        {/* Agreement to sign (fee-bearing clients) */}
        {agreement && ['sent', 'delivered'].includes(agreement.status) && (
          <section style={{ background: 'white', border: `1px solid ${t.gold}`, borderLeft: `4px solid ${t.gold}`, borderRadius: 10, padding: '1.1rem 1.25rem', margin: '1.5rem 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontWeight: 700, color: t.navy, fontSize: '1.05rem', fontFamily: t.fontSerif }}>
                <i className="bi bi-vector-pen me-2" />Your agreement is ready to sign
              </div>
              <div style={{ color: t.inkSubtle, fontSize: '0.85rem', marginTop: 3 }}>
                Please review and sign your Financial Planning &amp; Consulting Agreement.
              </div>
              {signErr && <div style={{ color: '#b00020', fontSize: '0.8rem', marginTop: 5 }}>{signErr}</div>}
            </div>
            <button type="button" onClick={handleSignAgreement} disabled={signing} style={{ background: t.navy, color: 'white', border: 0, borderRadius: 6, padding: '0.65rem 1.3rem', fontWeight: 700, fontSize: '0.9rem', cursor: signing ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}>
              {signing ? 'Opening…' : 'Review & sign'}
            </button>
          </section>
        )}
        {agreement && agreement.status === 'completed' && (
          <section style={{ background: 'rgba(31,110,74,0.06)', border: `1px solid ${t.success}`, borderLeft: `4px solid ${t.success}`, borderRadius: 10, padding: '0.9rem 1.25rem', margin: '1.5rem 0', color: t.navy, fontSize: '0.9rem', fontWeight: 600 }}>
            <i className="bi bi-check-circle-fill me-2" style={{ color: t.success }} />Your agreement is signed — thank you.
          </section>
        )}

        {/* ── The two halves of the portal ──────────────────────────────
            Named from the CLIENT's side of the exchange, not the firm's
            ("Shared with you", not "Documents from us") - everything else on
            this page is written that way too.

            The bar shows even for a brand-new client with nothing shared
            yet. A navigation that appears and disappears as documents arrive
            is more disorienting than an empty tab with a sentence in it, and
            showing both from day one is what tells someone this is a place
            documents come back to them, not just an upload form. */}
        <PortalTabs
          active={activeTab}
          onChange={setTab}
          tabs={[
            {
              key: 'requested',
              label: 'Requested from you',
              icon: 'bi-upload',
              // The count is what's still outstanding, so it empties as they
              // work - a total that never moves is just decoration.
              count: documentsSubmitted ? 0 : outstanding,
              done: totalDocs > 0 && outstanding === 0,
            },
            {
              key: 'shared',
              label: 'Shared with you',
              icon: 'bi-folder2-open',
              count: unreadShared,
              accent: true,   // unread from us is gold, not navy
            },
          ]}
        />

        {activeTab === 'shared' ? (
          <section
            id="portal-panel-shared"
            role="tabpanel"
            aria-labelledby="portal-tab-shared"
            style={{
              background: t.surface,
              border: `1px solid ${t.hairline}`,
              borderTop: `3px solid ${t.gold}`,
              borderRadius: 6,
              padding: '1.5rem 1.5rem 1.25rem',
            }}
          >
            <p style={{ color: t.gold, fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.22em', margin: 0 }}>
              From your advisory team
            </p>
            <h2 style={{
              fontFamily: t.fontSerif, fontSize: '1.35rem', fontWeight: 700,
              color: t.navy, margin: '0.3rem 0 0.4rem', letterSpacing: '-0.005em',
            }}>
              Your documents
            </h2>
            <p style={{ color: t.inkSubtle, fontSize: '0.82rem', margin: '0 0 1rem', maxWidth: 580, lineHeight: 1.55 }}>
              Documents we&apos;ve prepared and shared with you. They stay here, so you can
              come back to them any time.
            </p>

            {sharedError && (
              <div role="alert" style={{
                background: 'rgba(154,28,28,0.06)', border: '1px solid rgba(154,28,28,0.30)',
                color: t.error, borderRadius: 4, padding: '0.55rem 0.85rem',
                fontSize: '0.82rem', marginBottom: '0.75rem',
              }}>{sharedError}</div>
            )}

            {sharedDocs.length === 0 ? (
              <div style={{
                padding: '2rem 1.5rem', textAlign: 'center', color: t.muted,
                fontSize: '0.85rem', border: `1px dashed ${t.hairlineStrong}`, borderRadius: 6,
              }}>
                <i className="bi bi-folder2-open" style={{ fontSize: '1.6rem', opacity: 0.5, display: 'block', marginBottom: '0.5rem' }} />
                Nothing here yet. When your advisory team prepares something for you —
                a plan, a review packet, a statement — it will appear here and we&apos;ll email you.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                {sharedDocs.map(doc => (
                  <SharedDocumentRow
                    key={doc.id}
                    doc={doc}
                    // What THIS row is doing ('view' | 'download' | null), and
                    // whether anything anywhere is in flight — the second one
                    // quiets every other row's buttons too, so the portal only
                    // ever has one fetch running.
                    busy={openingShared?.id === doc.id ? openingShared.mode : null}
                    anyBusy={!!openingShared}
                    onView={() => openSharedDocument(doc)}
                    onDownload={() => openSharedDocument(doc, { download: true })}
                  />
                ))}
              </div>
            )}
          </section>
        ) : (
        <div id="portal-panel-requested" role="tabpanel" aria-labelledby="portal-tab-requested">
        {documentsSubmitted ? (
          <section style={{
            background: t.surface,
            border: `1px solid ${t.hairline}`,
            borderTop: `3px solid ${t.success}`,
            borderRadius: 6,
            padding: '2.75rem 1.5rem',
            textAlign: 'center',
          }}>
            <i className="bi bi-check-circle-fill" style={{ fontSize: '2.5rem', color: t.success, display: 'block', marginBottom: '0.85rem' }} />
            <h2 style={{
              fontFamily: t.fontSerif, fontSize: '1.5rem', fontWeight: 700,
              color: t.navy, margin: '0 0 0.5rem', letterSpacing: '-0.005em',
            }}>
              Thank you!
            </h2>
            <p style={{ color: t.inkSubtle, fontSize: '0.95rem', maxWidth: 480, margin: '0 auto 1.5rem', lineHeight: 1.55 }}>
              Your documents were submitted to{' '}
              <strong style={{ color: t.navy }}>
                {client.advisor_name ? `${client.advisor_name}'s` : 'your advisor’s'}
              </strong>{' '}
              office for review.
            </p>
            {submitError && (
              <div role="alert" style={{ color: t.error, fontSize: '0.82rem', marginBottom: '1rem' }}>{submitError}</div>
            )}
            <button
              type="button"
              onClick={handleEditDocuments}
              disabled={submitting}
              style={{
                background: 'transparent', color: t.navy, border: `1px solid ${t.hairlineStrong}`,
                borderRadius: 6, padding: '0.65rem 1.3rem', fontWeight: 700, fontSize: '0.9rem',
                cursor: submitting ? 'wait' : 'pointer',
              }}
            >
              {submitting ? 'Reopening…' : 'Edit existing documents'}
            </button>
          </section>
        ) : (
          <>
        {/* Step 1 — documents */}
        <section style={{
          background: t.surface,
          border: `1px solid ${t.hairline}`,
          borderTop: `3px solid ${t.navy}`,
          borderRadius: 6,
          padding: '1.5rem 1.5rem 1.25rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1.25rem' }}>
            <div>
              <p style={{ color: t.gold, fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.22em', margin: 0 }}>
                What&apos;s next
              </p>
              <h2 style={{
                fontFamily: t.fontSerif, fontSize: '1.35rem', fontWeight: 700,
                color: t.navy, margin: '0.3rem 0 0', letterSpacing: '-0.005em',
              }}>
                Step 1 &middot; Upload your documents
              </h2>
              <p style={{ color: t.inkSubtle, fontSize: '0.82rem', margin: '0.4rem 0 0', maxWidth: 580, lineHeight: 1.55 }}>
                These are the items your advisor needs to begin building your plan. You can
                upload now or come back any time — your progress is saved automatically.
              </p>
            </div>
            {/* "0 / 0 documents received" is a worse greeting than no counter
                at all, and a household set up only to receive documents sees
                exactly that. */}
            {totalDocs > 0 && (
              <div style={{
                textAlign: 'right',
                fontFamily: t.fontSerif,
                color: allUploaded ? t.success : t.navy,
                fontSize: '1.6rem',
                fontWeight: 700,
                lineHeight: 1,
              }}>
                {uploadedDocs}<span style={{ color: t.muted, fontFamily: t.fontSans, fontSize: '0.95rem', fontWeight: 500 }}> / {totalDocs}</span>
                <div style={{
                  color: t.muted, fontFamily: t.fontSans,
                  fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.18em',
                  marginTop: 4,
                }}>
                  Documents received
                </div>
              </div>
            )}
          </div>

          {uploadError && (
            <div role="alert" style={{
              background: 'rgba(154,28,28,0.06)',
              border: `1px solid rgba(154,28,28,0.30)`,
              color: t.error, borderRadius: 4,
              padding: '0.55rem 0.85rem',
              fontSize: '0.82rem',
              marginBottom: '0.75rem',
            }}>{uploadError}</div>
          )}

          {totalDocs === 0 ? (
            <div style={{
              padding: '1.5rem',
              textAlign: 'center',
              color: t.muted,
              fontSize: '0.85rem',
              fontStyle: 'italic',
              border: `1px dashed ${t.hairlineStrong}`,
              borderRadius: 6,
            }}>
              Your advisor hasn&apos;t requested any documents yet. They&apos;ll appear here once selected.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {orderedDocs.map(doc => (
                <DocumentCard
                  key={doc.id}
                  doc={doc}
                  isUploading={uploadingId === doc.id}
                  onFile={(file) => handleUpload(doc, file)}
                  onDeleteUpload={handleDeleteUpload}
                />
              ))}
            </div>
          )}
        </section>

        {/* General drag-and-drop — upload anything else without picking a
            category first; we'll read it and sort it for you. */}
        <section style={{
          background: t.surface,
          border: `1px solid ${t.hairline}`,
          borderTop: `3px solid ${t.gold}`,
          borderRadius: 6,
          padding: '1.5rem 1.5rem 1.25rem',
          marginTop: '1.25rem',
        }}>
          <p style={{ color: t.gold, fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.22em', margin: 0 }}>
            Have something else?
          </p>
          <h2 style={{
            fontFamily: t.fontSerif, fontSize: '1.15rem', fontWeight: 700,
            color: t.navy, margin: '0.3rem 0 0.4rem', letterSpacing: '-0.005em',
          }}>
            Additional documents / bulk upload
          </h2>
          <p style={{ color: t.inkSubtle, fontSize: '0.82rem', margin: '0 0 1rem', maxWidth: 580, lineHeight: 1.55 }}>
            Anything else you&apos;d like to share — you don&apos;t need to know which category it
            belongs in. Drop one or more files below and we&apos;ll read and sort them for you.
          </p>

          {generalUploadError && (
            <div role="alert" style={{
              background: 'rgba(154,28,28,0.06)', border: `1px solid rgba(154,28,28,0.30)`,
              color: t.error, borderRadius: 4, padding: '0.55rem 0.85rem', fontSize: '0.82rem',
              marginBottom: '0.75rem',
            }}>{generalUploadError}</div>
          )}

          <GeneralDropzone
            isDragging={isDraggingGeneral}
            uploadingCount={generalUploadingCount}
            onDragOver={(e) => { e.preventDefault(); setIsDraggingGeneral(true); }}
            onDragLeave={() => setIsDraggingGeneral(false)}
            onDrop={handleGeneralDrop}
            onPick={(files) => handleGeneralUpload(files)}
          />

          {generalUploads.length > 0 && (
            <div style={{ marginTop: '1rem', background: 'rgba(31,110,74,0.04)', borderRadius: 6, overflow: 'hidden' }}>
              {generalUploads.map(u => (
                <UploadedFileRow
                  key={u.id}
                  upload={u}
                  sorting={u.sorting}
                  onDelete={() => handleDeleteUpload(u)}
                />
              ))}
            </div>
          )}
        </section>

        {/* "I am done" — hands the checklist off to the office for review.
            Hidden when there is nothing to hand off: a household set up purely
            to RECEIVE documents (now a supported setup - the wizard no longer
            forces a request list) would otherwise be offered a button that
            notifies their advisor about nothing. */}
        {(totalDocs > 0 || generalUploads.length > 0) && (
        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          {submitError && (
            <div role="alert" style={{ color: t.error, fontSize: '0.82rem', marginBottom: '0.85rem' }}>{submitError}</div>
          )}
          <button
            type="button"
            onClick={handleSubmitDocuments}
            disabled={submitting}
            style={{
              background: t.navy, color: 'white', border: 0, borderRadius: 8,
              padding: '1rem 2.25rem', fontWeight: 700, fontSize: '1rem',
              cursor: submitting ? 'wait' : 'pointer',
              boxShadow: '0 4px 14px rgba(10,41,71,0.18)',
            }}
          >
            <i className="bi bi-check2-circle me-2" />
            {submitting ? 'Submitting…' : 'I am done uploading documents, notify my advisor'}
          </button>
        </div>
        )}
          </>
        )}
        </div>
        )}

        <p style={{ textAlign: 'center', color: t.muted, fontSize: '0.7rem', marginTop: '1.25rem', lineHeight: 1.55 }}>
          Your documents are stored securely and only your advisory team can access them.
          Files are encrypted in transit and at rest.
        </p>
      </main>

      {/* Footer */}
      <footer style={{
        borderTop: `1px solid ${t.hairline}`,
        background: t.surface,
        padding: '1rem 1.5rem',
        color: t.muted,
        fontSize: '0.65rem',
        textAlign: 'center',
      }}>
        © {new Date().getFullYear()} Prosperity Capital Advisors · Registered Investment Adviser
      </footer>

      {/* Closing gold accent (matches login) */}
      <div style={{
        height: 2, flexShrink: 0,
        background: `linear-gradient(90deg, transparent 0%, ${t.gold} 50%, transparent 100%)`,
      }} />

      <SharedDocumentViewer
        url={sharedViewer?.url}
        filename={sharedViewer?.filename}
        video={!!sharedViewer?.video}
        onClose={closeSharedViewer}
      />
    </div>
  );
}

// ─── Tabs ───────────────────────────────────────────────────────────────────
// An underline bar rather than pills or boxed tabs: this page is already a
// stack of bordered cards, and a second set of enclosing shapes would fight
// them. The active tab is navy with a gold rule under it - the same two marks
// the stepper and the card tops use, so the navigation reads as part of the
// same object rather than a control bolted on.
//
// A real tablist, not divs that look like one: arrow keys move between tabs,
// Home/End jump to the ends, and only the active tab is in the page's tab
// order (the WAI-ARIA pattern), so a keyboard user tabs past the navigation
// into the panel instead of through every tab on the way.
function PortalTabs({ tabs, active, onChange }) {
  const refs = useRef({});

  const onKeyDown = (e) => {
    const keys = tabs.map(x => x.key);
    const i = keys.indexOf(active);
    let next = null;
    if (e.key === 'ArrowRight') next = keys[(i + 1) % keys.length];
    else if (e.key === 'ArrowLeft') next = keys[(i - 1 + keys.length) % keys.length];
    else if (e.key === 'Home') next = keys[0];
    else if (e.key === 'End') next = keys[keys.length - 1];
    if (!next) return;
    e.preventDefault();
    onChange(next);
    refs.current[next]?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label="Your documents"
      onKeyDown={onKeyDown}
      style={{
        display: 'flex', gap: '0.25rem', flexWrap: 'wrap',
        borderBottom: `1px solid ${t.hairline}`,
        margin: '2rem 0 1.25rem',
      }}
    >
      {tabs.map((tabItem) => {
        const isActive = tabItem.key === active;
        // Gold for unread from us, navy for work outstanding, a quiet green
        // tick when a side is finished. Three states, three marks - the
        // number alone can't say "nothing left to do" as clearly as a tick.
        const badgeBg = tabItem.accent ? t.gold : t.navy;
        return (
          <button
            key={tabItem.key}
            ref={(el) => { refs.current[tabItem.key] = el; }}
            id={`portal-tab-${tabItem.key}`}
            role="tab"
            type="button"
            aria-selected={isActive}
            aria-controls={`portal-panel-${tabItem.key}`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(tabItem.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              background: 'transparent', border: 0,
              // The 2px rule sits on the button so it lands exactly on the
              // container's hairline, and transparent-when-inactive keeps
              // every tab the same height (no 2px jump on selection).
              borderBottom: `2px solid ${isActive ? t.gold : 'transparent'}`,
              marginBottom: -1,
              padding: '0.7rem 1rem',
              color: isActive ? t.navy : t.muted,
              fontWeight: isActive ? 700 : 600,
              fontSize: '0.88rem',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            <i className={`bi ${tabItem.icon}`} style={{ fontSize: '0.92rem', opacity: isActive ? 1 : 0.75 }} />
            {tabItem.label}
            {tabItem.count > 0 && (
              <span
                // Spoken as part of the tab's name, so a screen reader hears
                // "Shared with you, 2 new" rather than a bare number.
                aria-label={`${tabItem.count} new`}
                style={{
                  background: badgeBg, color: 'white',
                  borderRadius: 999, minWidth: 18, height: 18,
                  padding: '0 5px', fontSize: '0.68rem', fontWeight: 800,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  lineHeight: 1,
                }}
              >
                {tabItem.count}
              </span>
            )}
            {!tabItem.count && tabItem.done && (
              <i className="bi bi-check-circle-fill" aria-label="all done" style={{ color: t.success, fontSize: '0.85rem' }} />
            )}
          </button>
        );
      })}
    </div>
  );
}
PortalTabs.propTypes = {
  tabs: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.string,
    count: PropTypes.number,
    done: PropTypes.bool,
    accent: PropTypes.bool,
  })).isRequired,
  active: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

// ─── Documents shared WITH the client ───────────────────────────────────────
// One row per document the advisory team has sent. Download is always offered
// here, unlike the work-side lists: the app-wide `show_download_buttons`
// switch exists to stop staff pulling client files off the platform, and
// applying it to a client's own financial plan would be the opposite of what
// it is for.
// What KIND of file this is, and whether a browser can actually show it.
//
// Every document is fetched the same way — bytes into memory, handed over as a
// blob: URL — but only some formats render from there. An .xlsx handed to an
// <iframe> doesn't display, it downloads, so a button labelled "View" was
// describing something that never happened. The download is the right
// behaviour; the label was the problem.
//
// `label` earns its place separately: the portal shows each document's TITLE,
// not its filename, so without this the client has no way to know they're
// about to get a spreadsheet rather than a PDF.
const FILE_KINDS = [
  { exts: ['pdf'],                          label: 'PDF',   viewable: true,  opensIn: null },
  { exts: ['png', 'jpg', 'jpeg', 'webp'],   label: 'Image', viewable: true,  opensIn: null },
  { exts: ['txt'],                          label: 'Text',  viewable: true,  opensIn: null },
  // HEIC renders in Safari and nowhere else, so it is treated as a download —
  // better to promise less and have it work everywhere.
  { exts: ['heic'],                         label: 'Image', viewable: false, opensIn: 'your photo app' },
  { exts: ['xls', 'xlsx'],                  label: 'Excel', viewable: false, opensIn: 'Excel' },
  { exts: ['doc', 'docx'],                  label: 'Word',  viewable: false, opensIn: 'Word' },
  { exts: ['csv'],                          label: 'CSV',   viewable: false, opensIn: 'your spreadsheet app' },
  // Streams from a playback link into a <video>, never through a blob: URL —
  // see openSharedDocument. Mirrors isVideoFile in components/fileKinds.js.
  { exts: ['mp4', 'm4v', 'mov', 'webm'],    label: 'Video', viewable: true,  opensIn: null, video: true },
];

function fileKind(doc) {
  // Not `split('.').pop()`: on a name with no dot that returns the whole name,
  // so "Statement" would be labelled "STATEMENT". Mirrors fileExt() in
  // components/fileKinds.js, which this file can't import — see FILE_KINDS.
  const name = String(doc?.original_filename || '');
  const dot = name.lastIndexOf('.');
  const ext = dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
  const found = FILE_KINDS.find(k => k.exts.includes(ext));
  // Unknown extensions fall back to "downloads", never to "views": a View
  // button that silently downloads is the exact thing being fixed here.
  return found || { label: ext ? ext.toUpperCase() : 'File', viewable: false, opensIn: null };
}

// A spinner that inherits the button's text colour, so one component works on
// both the navy View button and the outlined Download one.
function Spinner({ size = 13 }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-block', width: size, height: size,
        border: '2px solid currentColor',
        borderRightColor: 'transparent',
        borderRadius: '50%',
        opacity: 0.85,
        animation: 'portal-spin 0.6s linear infinite',
        verticalAlign: '-2px',
      }}
    />
  );
}
Spinner.propTypes = { size: PropTypes.number };

function SharedDocumentRow({ doc, onView, onDownload, busy, anyBusy }) {
  // Unread is carried by a gold left edge and a small "New" mark, not by a
  // different background: a list where half the rows are tinted reads as two
  // kinds of document rather than one list with some unopened items. The flag
  // is this person's own view state - never anyone else's in the household.
  const isNew = !doc.viewed;
  const kind = fileKind(doc);
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '0.85rem', flexWrap: 'wrap',
      border: `1px solid ${t.hairline}`,
      borderLeft: `3px solid ${isNew ? t.gold : t.hairline}`,
      borderRadius: 6,
      padding: '0.8rem 1rem', background: 'rgba(200,169,81,0.04)',
    }}>
      <i className={`bi ${kind.video ? 'bi-play-btn' : 'bi-file-earmark-text'}`} style={{ fontSize: '1.3rem', color: t.gold, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 180 }}>
        <div style={{ fontWeight: 700, color: t.navy, fontSize: '0.92rem' }}>
          {doc.title}
          {doc.document_year && (
            <span style={{ color: t.muted, fontWeight: 600, fontSize: '0.78rem' }}> · {doc.document_year}</span>
          )}
          {isNew && (
            <span style={{
              marginLeft: '0.5rem', background: t.gold, color: t.navy,
              borderRadius: 999, padding: '1px 7px', fontSize: '0.62rem',
              fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em',
              verticalAlign: 'middle',
            }}>
              New
            </span>
          )}
        </div>
        <div style={{ color: t.muted, fontSize: '0.75rem', marginTop: 1 }}>
          {doc.type_label}
          {doc.shared_by_name && <> · shared by {doc.shared_by_name}</>}
          {/* Format and size together, so "Excel · 15 KB" sets the expectation
              before the button is pressed. */}
          <> · {kind.label}</>
          {doc.file_size_bytes ? <> · {formatBytes(doc.file_size_bytes)}</> : null}
        </div>
        {doc.note && (
          <div style={{ color: t.inkSubtle, fontSize: '0.8rem', marginTop: 5, lineHeight: 1.5 }}>
            {doc.note}
          </div>
        )}
      </div>
      {/* Buttons quiet down while anything is fetching, but only the one
          actually working shows a spinner — a row of spinners would say the
          whole list is loading rather than the one file they asked for. The
          widths are fixed so swapping a label in doesn't shuffle the row.

          A format the browser can't render gets ONE button, labelled
          Download, rather than a View that quietly downloads instead. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexShrink: 0 }}>
        {kind.viewable ? (
          <>
            <button
              type="button"
              onClick={onView}
              disabled={anyBusy}
              aria-busy={busy === 'view'}
              style={{
                background: t.navy, color: 'white', border: 0, borderRadius: 6,
                padding: '0.45rem 0.9rem', fontWeight: 700, fontSize: '0.8rem',
                cursor: anyBusy ? 'wait' : 'pointer',
                opacity: anyBusy && busy !== 'view' ? 0.5 : 1,
                minWidth: 96, textAlign: 'center',
              }}
            >
              {busy === 'view'
                ? <><Spinner /> <span style={{ marginLeft: 6 }}>Opening…</span></>
                : kind.video
                  ? <><i className="bi bi-play-fill me-1" />Watch</>
                  : <><i className="bi bi-eye me-1" />View</>}
            </button>
            <button
              type="button"
              onClick={onDownload}
              disabled={anyBusy}
              aria-busy={busy === 'download'}
              aria-label={busy === 'download' ? `Preparing ${doc.title}` : `Download ${doc.title}`}
              style={{
                background: 'transparent', color: t.navy, border: `1px solid ${t.hairlineStrong}`,
                borderRadius: 6, padding: '0.45rem 0.8rem', fontWeight: 700, fontSize: '0.8rem',
                cursor: anyBusy ? 'wait' : 'pointer',
                opacity: anyBusy && busy !== 'download' ? 0.5 : 1,
                minWidth: 44, textAlign: 'center',
              }}
            >
              {busy === 'download' ? <Spinner /> : <i className="bi bi-download" />}
            </button>
          </>
        ) : (
          <>
            {kind.opensIn && (
              <span style={{ color: t.muted, fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
                Opens in {kind.opensIn}
              </span>
            )}
            <button
              type="button"
              onClick={onDownload}
              disabled={anyBusy}
              aria-busy={busy === 'download'}
              title={kind.opensIn
                ? `${kind.label} files download to your device and open in ${kind.opensIn}`
                : 'Downloads to your device'}
              style={{
                background: t.navy, color: 'white', border: 0, borderRadius: 6,
                padding: '0.45rem 0.9rem', fontWeight: 700, fontSize: '0.8rem',
                cursor: anyBusy ? 'wait' : 'pointer',
                opacity: anyBusy && busy !== 'download' ? 0.5 : 1,
                minWidth: 116, textAlign: 'center',
              }}
            >
              {busy === 'download'
                ? <><Spinner /> <span style={{ marginLeft: 6 }}>Preparing…</span></>
                : <><i className="bi bi-download me-1" />Download</>}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
SharedDocumentRow.propTypes = {
  doc: PropTypes.object.isRequired,
  onView: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  // What this row is fetching, if anything: 'view' | 'download' | null.
  busy: PropTypes.oneOf(['view', 'download', null]),
  // Whether any row is fetching — every button waits while one is.
  anyBusy: PropTypes.bool,
};

// In-page viewer over a blob: URL. The portal keeps its own rather than
// importing the work side's DocumentViewerModal — this directory imports
// nothing from routes/, app/ or components/, so that it can be lifted into its
// own app unchanged.
function SharedDocumentViewer({ url, filename, onClose, video = false }) {
  if (!url) return null;
  return (
    <div
      onClick={onClose}
      role="presentation"
      style={{
        position: 'fixed', inset: 0, zIndex: 1400,
        background: 'rgba(10,41,71,0.65)',
        display: 'flex', flexDirection: 'column', padding: '1.5rem',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={filename || 'Document'}
        style={{
          background: 'white', borderRadius: 8, flex: 1,
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 12px 40px rgba(10,41,71,0.35)',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0.65rem 1rem', borderBottom: `1px solid ${t.hairline}`, flexShrink: 0,
        }}>
          <span style={{
            fontWeight: 700, color: t.navy, fontSize: '0.9rem',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {filename || 'Document'}
          </span>
          <button
            type="button" onClick={onClose} aria-label="Close"
            style={{
              background: 'none', border: 0, color: t.muted, fontSize: '1.2rem',
              cursor: 'pointer', lineHeight: 1, padding: '0.25rem',
            }}
          >
            <i className="bi bi-x-lg" />
          </button>
        </div>
        {video ? (
          <div style={{ flex: 1, minHeight: 0, background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {/* nodownload keeps saving on the Download button, which is what
                counts it as a download for the advisor's read receipt. */}
            <video
              src={url} controls autoPlay playsInline controlsList="nodownload"
              style={{ maxWidth: '100%', maxHeight: '100%' }}
            />
          </div>
        ) : (
          <iframe title={filename || 'Document'} src={url} style={{ flex: 1, border: 0, width: '100%' }} />
        )}
      </div>
    </div>
  );
}
SharedDocumentViewer.propTypes = {
  url: PropTypes.string,
  filename: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  // url is a streaming playback link for a <video>, not a blob: URL.
  video: PropTypes.bool,
};

// ─── Stepper ───────────────────────────────────────────────────────────────
function WorkflowStepper({ currentIdx }) {
  return (
    <div style={{
      background: t.surface,
      border: `1px solid ${t.hairline}`,
      borderRadius: 6,
      padding: '1.25rem 1.5rem',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
        {WORKFLOW_STAGES.map((s, i) => {
          const done = i < currentIdx;
          const current = i === currentIdx;
          return (
            <React.Fragment key={s.key}>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: done ? t.success : current ? t.navy : t.surface,
                  border: done ? `2px solid ${t.success}` : current ? `2px solid ${t.navy}` : `2px solid ${t.hairlineStrong}`,
                  color: done || current ? 'white' : t.muted,
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  fontFamily: t.fontSerif,
                  boxShadow: current ? `0 0 0 6px rgba(10,41,71,0.06)` : 'none',
                  transition: 'all 0.18s',
                  flexShrink: 0,
                }}>
                  {done ? <i className="bi bi-check-lg" /> : i + 1}
                </div>
                <div style={{
                  marginTop: '0.55rem',
                  fontFamily: t.fontSerif,
                  fontSize: '0.95rem',
                  fontWeight: 700,
                  color: current ? t.navy : t.inkSubtle,
                  letterSpacing: '-0.005em',
                }}>
                  {s.label}
                </div>
                <div style={{
                  marginTop: 2,
                  fontSize: '0.65rem',
                  color: current ? t.muted : t.hairlineStrong,
                  textTransform: 'uppercase',
                  letterSpacing: '0.12em',
                  fontWeight: 600,
                }}>
                  {done ? 'Complete' : current ? 'In progress' : 'Up next'}
                </div>
              </div>
              {i < WORKFLOW_STAGES.length - 1 && (
                <div style={{
                  height: 2,
                  flex: '0 0 2.5rem',
                  background: i < currentIdx ? t.success : t.hairline,
                  marginTop: -28, // sit at the level of the circles
                }} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
WorkflowStepper.propTypes = { currentIdx: PropTypes.number.isRequired };

// ─── Advisor card ──────────────────────────────────────────────────────────
// One advisor inside the card: a headshot (or initials monogram fallback)
// alongside a label + name. `small` shrinks the row for the second advisor.
function AdvisorRow({ label, name, photoUrl, small }) {
  const initials = name.split(/\s+/).map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  const size = small ? 34 : 42;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
      {photoUrl ? (
        <img
          src={photoUrl}
          alt={name}
          style={{
            width: size, height: size, borderRadius: '50%',
            objectFit: 'cover', flexShrink: 0,
            border: `1px solid ${t.hairline}`,
          }}
        />
      ) : (
        <div style={{
          width: size, height: size, borderRadius: '50%',
          background: t.navy,
          color: t.goldSoft,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: t.fontSerif, fontWeight: 700, fontSize: small ? '0.8rem' : '0.95rem',
          flexShrink: 0,
        }}>
          {initials || 'PCA'}
        </div>
      )}
      <div style={{ minWidth: 0 }}>
        <div style={{
          color: t.muted, fontSize: small ? '0.55rem' : '0.6rem', fontWeight: 600,
          textTransform: 'uppercase', letterSpacing: '0.18em',
        }}>
          {label}
        </div>
        <div style={{ color: t.navy, fontFamily: t.fontSerif, fontSize: small ? '0.8rem' : '1rem', fontWeight: 700, marginTop: small ? 1 : 2 }}>
          {name}
        </div>
      </div>
    </div>
  );
}
AdvisorRow.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  photoUrl: PropTypes.string,
  small: PropTypes.bool,
};

function AdvisorCard({ name, photoUrl, secondName, secondPhotoUrl }) {
  return (
    <div style={{
      background: t.surface,
      border: `1px solid ${t.hairline}`,
      borderRadius: 6,
      padding: '0.85rem 1.1rem',
      display: 'flex', flexDirection: 'column', gap: '0.65rem',
      minWidth: 240,
    }}>
      <AdvisorRow label="Your advisor" name={name} photoUrl={photoUrl} />
      {secondName && (
        <>
          <div style={{ borderTop: `1px solid ${t.hairline}` }} />
          <AdvisorRow label="Second advisor" name={secondName} photoUrl={secondPhotoUrl} small />
        </>
      )}
    </div>
  );
}
AdvisorCard.propTypes = {
  name: PropTypes.string.isRequired,
  photoUrl: PropTypes.string,
  secondName: PropTypes.string,
  secondPhotoUrl: PropTypes.string,
};

// ─── Document card (one per requested category — many uploads per card) ───
function DocumentCard({ doc, isUploading, onFile, onDeleteUpload }) {
  const fileInputRef = useRef(null);
  const uploads = doc.uploads || [];
  const hasUploads = uploads.length > 0;

  return (
    <div style={{
      border: `1px solid ${t.hairline}`,
      borderLeft: `3px solid ${hasUploads ? t.success : t.hairlineStrong}`,
      borderRadius: 6,
      background: t.surface,
      overflow: 'hidden',
    }}>
      {/* Card header — category name + status pill + upload button */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.85rem',
        padding: '0.85rem 1.1rem',
        borderBottom: hasUploads ? `1px solid ${t.hairline}` : 'none',
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: '50%',
          background: hasUploads ? t.success : 'rgba(10,41,71,0.05)',
          color: hasUploads ? 'white' : t.navy,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
          fontSize: '0.85rem',
        }}>
          <i className={`bi bi-${hasUploads ? 'check-lg' : 'file-earmark'}`} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: t.ink, fontWeight: 600, fontSize: '0.88rem', fontFamily: t.fontSans }}>
            {labelForDoc(doc)}
          </div>
          <div style={{ color: t.muted, fontSize: '0.72rem', marginTop: 2 }}>
            {hasUploads
              ? `${uploads.length} file${uploads.length === 1 ? '' : 's'} received`
              : 'Not yet uploaded'}
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) onFile(f);
          }}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          style={{
            background: hasUploads ? 'transparent' : t.navy,
            color: hasUploads ? t.teal : 'white',
            border: hasUploads ? `1px solid ${t.hairlineStrong}` : 'none',
            borderRadius: 4,
            padding: '0.45rem 0.85rem',
            fontWeight: 700,
            fontSize: '0.72rem',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            cursor: isUploading ? 'wait' : 'pointer',
            fontFamily: t.fontSans,
            whiteSpace: 'nowrap',
          }}
        >
          {isUploading
            ? 'Uploading…'
            : hasUploads
              ? <><i className="bi bi-plus-lg me-1" /> Add another</>
              : <><i className="bi bi-upload me-1" /> Upload</>}
        </button>
      </div>

      {/* Uploaded files list */}
      {hasUploads && (
        <div style={{ background: 'rgba(31,110,74,0.04)' }}>
          {uploads.map(u => (
            <UploadedFileRow key={u.id} upload={u} onDelete={() => onDeleteUpload(u)} />
          ))}
        </div>
      )}
    </div>
  );
}
DocumentCard.propTypes = {
  doc: PropTypes.object.isRequired,
  isUploading: PropTypes.bool,
  onFile: PropTypes.func.isRequired,
  onDeleteUpload: PropTypes.func.isRequired,
};

function UploadedFileRow({ upload, onDelete, sorting }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '0.7rem',
      padding: '0.55rem 1.1rem 0.55rem 3.25rem',
      borderTop: `1px solid rgba(31,110,74,0.10)`,
      fontSize: '0.78rem',
    }}>
      <i
        className={`bi bi-${sorting ? 'hourglass-split' : 'check-circle-fill'}`}
        style={{ color: sorting ? t.gold : t.success, flexShrink: 0 }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: t.ink, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {upload.original_filename || 'Uploaded file'}
        </div>
        <div style={{ color: t.muted, fontSize: '0.7rem', marginTop: 1, display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
          {sorting
            ? <span style={{ color: t.gold, fontWeight: 600 }}>Sorting…</span>
            : <span style={{ color: t.success, fontWeight: 600 }}>Uploaded</span>}
          {upload.file_size_bytes != null && <span>{formatBytes(upload.file_size_bytes)}</span>}
          {upload.created && <span>{new Date(upload.created).toLocaleDateString()}</span>}
        </div>
      </div>
      <button
        type="button"
        onClick={onDelete}
        aria-label="Remove this file"
        title="Remove"
        style={{
          background: 'transparent', border: 'none', color: t.muted, cursor: 'pointer',
          padding: '0.25rem 0.4rem', fontSize: '0.85rem',
        }}
      >
        <i className="bi bi-x-lg" />
      </button>
    </div>
  );
}
UploadedFileRow.propTypes = {
  upload: PropTypes.object.isRequired,
  onDelete: PropTypes.func.isRequired,
  sorting: PropTypes.bool,
};

// Drag-and-drop target for the general upload area. Also a click-to-browse
// fallback (hidden multi-file input) since drag-and-drop alone excludes
// keyboard/touch users. Purely presentational — all state lives in the parent.
function GeneralDropzone({ isDragging, uploadingCount, onDragOver, onDragLeave, onDrop, onPick }) {
  const inputRef = useRef(null);
  const isUploading = uploadingCount > 0;
  return (
    <div
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onClick={() => inputRef.current?.click()}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inputRef.current?.click(); } }}
      role="button"
      tabIndex={0}
      style={{
        border: `1.5px dashed ${isDragging ? t.teal : t.hairlineStrong}`,
        borderRadius: 6,
        background: isDragging ? 'rgba(0,105,166,0.05)' : 'rgba(10,41,71,0.015)',
        padding: '1.5rem 1rem',
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'border-color 0.15s, background 0.15s',
      }}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => {
          // Copy out of the FileList BEFORE clearing the input. `e.target.files`
          // is live: resetting `value` empties that same object, so holding the
          // list across the reset hands onPick an empty one and nothing uploads.
          // The reset itself has to stay — without it, picking the same file
          // twice in a row fires no change event.
          const files = Array.from(e.target.files || []);
          e.target.value = '';
          if (files.length) onPick(files);
        }}
      />
      <i
        className={`bi bi-${isUploading ? 'arrow-repeat' : 'cloud-arrow-up'}`}
        style={{ fontSize: '1.6rem', color: isDragging ? t.teal : t.muted }}
      />
      <div style={{ color: t.ink, fontWeight: 600, fontSize: '0.85rem', marginTop: '0.5rem' }}>
        {isUploading
          ? `Uploading${uploadingCount > 1 ? ` ${uploadingCount} files` : '…'}`
          : 'Drag files here, or click to browse'}
      </div>
      <div style={{ color: t.muted, fontSize: '0.72rem', marginTop: '0.2rem' }}>
        You can drop multiple files at once. Up to {MAX_UPLOAD_LABEL} per file.
      </div>
    </div>
  );
}
GeneralDropzone.propTypes = {
  isDragging: PropTypes.bool,
  uploadingCount: PropTypes.number,
  onDragOver: PropTypes.func.isRequired,
  onDragLeave: PropTypes.func.isRequired,
  onDrop: PropTypes.func.isRequired,
  onPick: PropTypes.func.isRequired,
};

// Static catalog lookup so the row shows the friendly label, not the
// snake_case key. Mirrors the server's catalog — kept here as a small
// inline map so the portal doesn't need an extra round-trip just to
// render names.
const DOC_LABELS = {
  bank_checking: 'Checking', bank_savings: 'Savings',
  bank_money_market: 'Money Market Accounts', bank_cds: 'Certificates of Deposit',
  inv_ira: 'IRA statements', inv_roth_ira: 'Roth IRA statements',
  inv_401k: '401(k) statements', inv_403b: '403(b) statements',
  inv_tsp: 'TSP statements', inv_hsa: 'HSA statements',
  inv_529: '529 plan statements', inv_qualified_other: 'Other qualified plan statements',
  // Legacy lumped key — kept so older requests still render a friendly label.
  inv_qualified_plans: 'IRA / Roth / 401(k) / 403(b) / TSP / HSA / 529 qualified plans',
  inv_annuities: 'Annuity policies and statements',
  inv_stocks_bonds: 'Individual stocks and bonds',
  inv_brokerage: 'Brokerage accounts',
  inv_alternative: 'Alternative investments',
  pension_statements: 'Pension statements',
  social_security: 'Social Security statements',
  ltc_policy: 'Long-term care policy',
  disability_policy: 'Disability policy',
  life_insurance: 'Life insurance policies and statements',
  fe_funeral_expenses: 'Funeral expenses',
  fe_expense_trust: 'Expense trust',
  fe_burial_info: 'Burial information / policies',
  fe_prepaid_arrangements: 'Prepaid funeral arrangements',
  legal_will: 'Will',
  legal_power_of_attorney: 'Power of Attorney',
  legal_healthcare_poa: 'Healthcare Power of Attorney',
  legal_living_will: 'Living Will',
  legal_trust: 'Trust',
  legal_birth_certificate: 'Birth certificate',
  legal_marriage_license: 'Marriage license',
  prop_deed: 'Deed (home / real estate / timeshare)',
  prop_vehicle_titles: 'Vehicle / boat titles',
  prop_insurance_decl: 'Home / Auto / Umbrella insurance (declaration page)',
  debt_mortgage: 'Most recent mortgage statement',
  debt_car_loan: 'Car loan statements',
  debt_other_loans: 'Other loans',
  debt_credit_card: 'Credit card statements',
  tax_current_year: 'Current-year tax return',
  tax_w2_paystub: 'W-2 and recent paystub',
  medical_rx_policies: 'Medical / Rx policies',
  misc_passwords: 'Passwords to all accounts',
  misc_deposit_boxes: 'Location of safe-deposit boxes',
  misc_keys: 'Location of keys',
  misc_contact_upon_death: 'People to contact upon death',
  misc_inventory: 'Household inventory records',
  general_unsorted: 'Additional document (uncategorized)',
};
function labelForDoc(doc) {
  // Ad-hoc requests carry their description in custom_label; catalog items
  // resolve through the static label map.
  return doc.custom_label || DOC_LABELS[doc.type_key] || doc.type_key;
}

// ─── Stub views ────────────────────────────────────────────────────────────
function LoadingView() {
  return (
    <div style={{ minHeight: '100vh', background: t.canvas, display: 'flex', alignItems: 'center', justifyContent: 'center', color: t.muted, fontFamily: t.fontSans }}>
      Loading your portal…
    </div>
  );
}
function ErrorView({ message, onSignOut }) {
  return (
    <div style={{ minHeight: '100vh', background: t.canvas, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '1rem', color: t.ink, fontFamily: t.fontSans, padding: '2rem' }}>
      <div style={{ fontSize: '0.95rem' }}>{message}</div>
      <Link to="/portal/login" onClick={onSignOut} style={{ color: t.teal, fontSize: '0.85rem' }}>Return to sign in</Link>
    </div>
  );
}
ErrorView.propTypes = { message: PropTypes.string, onSignOut: PropTypes.func };
